import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useIsMobile } from "@/hooks/use-mobile";
import type { Retailer, BundleKitType, BundleVersion } from "@/contracts/bundles";

interface BundleFiltersProps {
  retailers: Retailer[];
  kitTypes: BundleKitType[];
  versions: BundleVersion[];
  selectedRetailers: string[];
  selectedKitTypes: string[];
  selectedVersions: string[];
  onRetailerToggle: (slug: string) => void;
  onKitTypeToggle: (slug: string) => void;
  onVersionToggle: (slug: string) => void;
  onClearFilters: () => void;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function BundleFilters({
  retailers,
  kitTypes,
  versions,
  selectedRetailers,
  selectedKitTypes,
  selectedVersions,
  onRetailerToggle,
  onKitTypeToggle,
  onVersionToggle,
  onClearFilters,
  open,
  onOpenChange,
}: BundleFiltersProps) {
  const isMobile = useIsMobile();
  const hasActiveFilters = 
    selectedRetailers.length > 0 || 
    selectedKitTypes.length > 0 || 
    selectedVersions.length > 0;

  const filtersContent = (
    <>
      {/* Retailers */}
      <div>
        <h4 className="font-semibold text-sm mb-2">Retailer</h4>
        <div className="space-y-2">
          {retailers.map((retailer) => (
            <div key={retailer.id} className="flex items-center space-x-2">
              <Checkbox
                id={`retailer-${retailer.slug}`}
                checked={selectedRetailers.includes(retailer.slug)}
                onCheckedChange={() => onRetailerToggle(retailer.slug)}
              />
              <Label
                htmlFor={`retailer-${retailer.slug}`}
                className="text-sm font-normal cursor-pointer"
              >
                {retailer.name}
              </Label>
            </div>
          ))}
        </div>
      </div>

      <Separator />

      {/* Versions */}
      <div>
        <h4 className="font-semibold text-sm mb-2">Version</h4>
        <div className="flex flex-wrap gap-2">
          {versions.map((version) => (
            <Badge
              key={version.id}
              variant={selectedVersions.includes(version.slug) ? "default" : "outline"}
              className="cursor-pointer hover:bg-primary/90"
              onClick={() => onVersionToggle(version.slug)}
              style={{
                backgroundColor: selectedVersions.includes(version.slug) && version.badge_color
                  ? version.badge_color
                  : undefined,
              }}
            >
              {version.name}
            </Badge>
          ))}
        </div>
      </div>

      <Separator />

      {/* Kit Types */}
      <div>
        <h4 className="font-semibold text-sm mb-2">Kit Type</h4>
        <div className="space-y-2 max-h-60 overflow-y-auto">
          {kitTypes.map((kitType) => (
            <div key={kitType.id} className="flex items-center space-x-2">
              <Checkbox
                id={`kit-${kitType.slug}`}
                checked={selectedKitTypes.includes(kitType.slug)}
                onCheckedChange={() => onKitTypeToggle(kitType.slug)}
              />
              <Label
                htmlFor={`kit-${kitType.slug}`}
                className="text-sm font-normal cursor-pointer"
              >
                {kitType.name}
              </Label>
            </div>
          ))}
        </div>
      </div>
    </>
  );

  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="left" className="w-[300px] overflow-y-auto p-6">
          <SheetHeader className="mb-6">
            <SheetTitle className="flex items-center justify-between">
              <span>Filters</span>
              {hasActiveFilters && (
                <button
                  onClick={onClearFilters}
                  className="text-xs text-primary hover:underline font-normal"
                >
                  Clear all
                </button>
              )}
            </SheetTitle>
          </SheetHeader>
          <div className="space-y-6">
            {filtersContent}
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Card className="sticky top-4">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">Filters</CardTitle>
          {hasActiveFilters && (
            <button
              onClick={onClearFilters}
              className="text-xs text-primary hover:underline"
            >
              Clear all
            </button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {filtersContent}
      </CardContent>
    </Card>
  );
}
